import type {SeriesOptionsType} from 'highcharts';
import {escapeHtml, formatValue} from '../render/format';
import {isLowBase, lowBaseAttributes} from '../render/lowBase';
import {MarkRenderer} from '../types';
import {buildHierarchy, nodeTooltip, sizeDetailsHtml} from './hierarchy';

const renderSunburst: MarkRenderer = (cube, spec, ctx) => {
    const hierarchy = buildHierarchy(cube, spec, ctx);
    const measure = hierarchy.sizeMeasure;
    const rootId = 'root';
    const labels = spec.options.dataLabels !== false;

    const data: Record<string, any>[] = hierarchy.nodes.map(node => {
        const lowBase = node.leaf ? isLowBase(cube, node.leaf.sizeCell) : false;
        const tooltip = nodeTooltip(node, hierarchy, ctx, true) + sizeDetailsHtml(node, measure, lowBase, ctx);

        return {
            id: node.id,
            parent: node.parentId ?? rootId,
            name: escapeHtml(node.label),
            // Inner nodes are summed by Highcharts from their children
            ...(node.leaf ? {value: node.leaf.size} : {}),
            // Deeper levels inherit the branch colour and vary its brightness (see levels)
            ...(node.depth === 0 ? {color: node.rootColor} : {}),
            ...(lowBase ? lowBaseAttributes('pie', node.rootColor, ctx) : {}),
            custom: {lowBase, tooltip},
        };
    });
    // Single root so the first path level forms the first ring and drill-up has a target
    const total = hierarchy.nodes.filter(node => node.depth === 0).reduce((sum, node) => sum + node.total, 0);
    data.unshift({
        id: rootId,
        parent: '',
        name: escapeHtml(measure.label),
        color: 'transparent',
        dataLabels: {enabled: false},
        custom: {tooltip: `${escapeHtml(measure.label)}: <b>${formatValue(total, measure, ctx)}</b>`},
    });

    return {
        type: 'highcharts',
        options: {
            chart: {type: 'sunburst'},
            title: {text: undefined},
            credits: {enabled: false},
            legend: {enabled: false},
            tooltip: {headerFormat: '', pointFormat: '{point.custom.tooltip}'},
            series: [{
                type: 'sunburst',
                id: measure.id,
                name: escapeHtml(measure.label),
                data,
                allowTraversingTree: hierarchy.dimensionIds.length > 1,
                cursor: hierarchy.dimensionIds.length > 1 ? 'pointer' : undefined,
                // Level 1 is the invisible root, the path rings are 2..4; only the first two rings get labels
                levels: [
                    {level: 1, levelIsConstant: false, dataLabels: {enabled: false}},
                    {level: 2, colorByPoint: true, dataLabels: {enabled: labels, rotationMode: 'auto'}},
                    {level: 3, colorVariation: {key: 'brightness', to: 0.25}, dataLabels: {enabled: labels, rotationMode: 'auto'}},
                    {level: 4, colorVariation: {key: 'brightness', to: -0.25}, dataLabels: {enabled: false}},
                ],
                dataLabels: {
                    enabled: labels,
                    format: '{point.name}',
                    style: {color: 'contrast', textOutline: 'none', fontWeight: 'normal'},
                },
            } as unknown as SeriesOptionsType],
        },
    };
};

export default renderSunburst;
